/**
 * Heat: how much the fight is worth watching, per fighter.
 *
 * Momentum already measures *doing well*. Heat measures something else, and the
 * difference is the point of having both: a fighter can win every exchange with
 * the same jab for two minutes and have a full meter and no heat at all. What
 * puts heat on is doing something worth telling somebody about — a finish off a
 * wall, a counter, a taunt held in front of a live opponent, a comeback won from
 * the floor. What takes it off is the opposite: a workmanlike knockout, backing
 * away, and throwing the same thing over and over.
 *
 * Two of the nine sources are not discrete events anybody can report, and they
 * are the reason this module holds state rather than being a number somebody
 * else adds to:
 *
 *   **fleeing** is a duration, not a moment. It is only running away once it has
 *   gone on for a while, so heat is told *whether the fighter is retreating* on
 *   every frame and decides for itself when that has become cowardice.
 *   **repetition** is a property of a sequence. Nothing else in the game
 *   remembers what was thrown three moves ago, and nothing else should have to.
 *
 * Everything that moves the meter also lands in a short rolling list, newest
 * first, because a number that changed is far less use than the reason it did.
 *
 * What heat *does* is drive momentum. `momentumScale` is the multiplier a hot
 * fighter's meter fills at, and it stacks with charisma — charisma is who the
 * fighter is, heat is how the night is going, and the two multiply because a
 * loud fighter in a hot fight really should fill it faster than either alone.
 * The relationship is a curve in `heat.json`, not a formula in here, so it can
 * be bent without touching code.
 *
 * Pure — no three.js, no timers of its own. Feed it `dt`.
 */

import HEAT from './heat.json';

export { HEAT };

/**
 * Sources that make a fighter more worth watching.
 *
 * `home` is the one that is not something they did in the round: it is the
 * crowd in a room they hold, on their side at the bell (`venues.js`). Recorded
 * once, when the round goes live, and then it drifts back with everything else
 * — a home crowd is a head start, not a second health bar.
 */
export const RISES = Object.freeze([
  'environmentalFinish',
  'weapon',
  'counter',
  'taunt',
  'blazin',
  'dangerWin',
  'home',
]);

/**
 * And less.
 *
 * `decision` is the one that needs saying. A fight that went the distance is
 * worth less to a crowd than any finish — but **`plainWin` stays the floor**,
 * because the dullest thing anybody can do in this game is win it with one
 * ordinary strike and no story. So the order is
 * `plainWin < decision < every finish worth watching`, and
 * `heat.test.js` asserts exactly that rather than trusting the file.
 *
 * `stale` is `repetition` for the wardrobe (step 93): walking out in a look
 * the crowd has seen too often lately (`bank.boredom`). Recorded once, at the
 * bell, beside the gear's charisma fading — one boredom, read two ways, and
 * no second meter for it.
 *
 * `ducked` is the odd one, because nothing happened: it is turning the reach
 * card down again (step 59). It sits **below `decision`** and the validator says
 * so — going the distance is a disappointment, and not turning up for the fight
 * the room wanted is worse than a disappointment.
 */
export const FALLS = Object.freeze(['plainWin', 'decision', 'ducked', 'flee', 'repetition', 'stale']);

export const SOURCES = Object.freeze([...RISES, ...FALLS]);

export class HeatConfigError extends Error {}

/**
 * Throws on a tuning table that would make heat mean something other than what
 * it says. A rise that is worth nothing, or a fall that is worth *more* than
 * nothing, is a table that has been edited into disagreeing with itself.
 */
export function validateHeat(config = HEAT) {
  if (!(config.max > 0)) throw new HeatConfigError('heat needs a positive max');
  for (const field of ['initial', 'baseline']) {
    const value = config[field];
    if (!(value >= 0 && value <= config.max)) {
      throw new HeatConfigError(`heat's ${field} (${value}) is outside 0..${config.max}`);
    }
  }
  if (!(config.history >= 1)) throw new HeatConfigError('heat needs to remember at least one event');

  for (const source of RISES) {
    if (!(config.events?.[source] > 0)) {
      throw new HeatConfigError(`"${source}" is a rise; it must be worth more than nothing`);
    }
  }
  for (const source of FALLS) {
    if (!(config.events?.[source] < 0)) {
      throw new HeatConfigError(`"${source}" is a fall; it must be negative`);
    }
  }
  // The one ordering the table cannot be allowed to lose. A decision duller
  // than a plain knockout would make going the distance the worst thing a
  // fighter can do, and a decision worth more than a finish would make the
  // bell the best way to win.
  const events = config.events ?? {};
  if (!(events.plainWin < events.decision)) {
    throw new HeatConfigError(
      `a plain win (${events.plainWin}) must stay duller than a decision (${events.decision})`
    );
  }
  // Not showing up is worse for a room than going the distance in front of it.
  if (!(events.ducked < events.decision)) {
    throw new HeatConfigError(
      `ducking a fight (${events.ducked}) has to cost more than a decision (${events.decision})`
    );
  }
  for (const finish of ['environmentalFinish', 'dangerWin']) {
    if (!(events.decision < events[finish])) {
      throw new HeatConfigError(
        `a decision (${events.decision}) must be worth less than ${finish} (${events[finish]})`
      );
    }
  }
  for (const source of Object.keys(config.events ?? {})) {
    if (!SOURCES.includes(source)) {
      throw new HeatConfigError(`"${source}" is not a heat source`);
    }
  }
  if (!(config.levels?.cold < config.levels?.hot)) {
    throw new HeatConfigError('cold has to be below hot');
  }
  validateCurve(config.momentum?.curve, config.max);
  return config;
}

/**
 * The heat-to-momentum relationship, as points rather than a formula.
 *
 * A curve is the right shape for this because the interesting part is not the
 * slope, it is the *bend*: the scale has to be 1.0 at the resting point, so
 * that adding heat to the game does not quietly rebalance every fight that was
 * already tuned, and it has to fall off faster below that than it climbs above.
 * Both of those are two numbers in a table and neither is a line of code.
 */
export function validateCurve(curve, max) {
  if (!Array.isArray(curve) || curve.length < 2) {
    throw new HeatConfigError('the momentum curve needs at least two points');
  }
  let previous = -Infinity;
  for (const point of curve) {
    if (!Array.isArray(point) || point.length !== 2) {
      throw new HeatConfigError('a momentum curve point is not [heat, scale]');
    }
    const [at, scale] = point;
    if (!Number.isFinite(at) || at < 0 || at > max) {
      throw new HeatConfigError(`momentum curve point at ${at} is outside 0..${max}`);
    }
    if (at <= previous) {
      throw new HeatConfigError(`momentum curve is not sorted: ${at} follows ${previous}`);
    }
    if (!(scale > 0)) {
      throw new HeatConfigError(`momentum scale at ${at} must be positive, got ${scale}`);
    }
    previous = at;
  }
  // Both ends, so no fighter's heat can fall off the table and be extrapolated.
  if (curve[0][0] !== 0 || curve[curve.length - 1][0] !== max) {
    throw new HeatConfigError(`the momentum curve has to span 0..${max}`);
  }
  return curve;
}

/** Straight lines between the points, flat outside them. */
/**
 * Which band a heat value falls in: `cold`, `warm` or `hot`.
 *
 * A function rather than only a getter on the meter, because a fighter is not
 * the only thing that wants the answer — Sala reads the room off the player's
 * number (step 62) and holds no meter. The comment on `level` has always said
 * a threshold compared in three places is three places to edit, and this is
 * how that stays true once there are two readers.
 */
export function levelOf(value, config = HEAT) {
  if (value <= config.levels.cold) return 'cold';
  if (value >= config.levels.hot) return 'hot';
  return 'warm';
}

export function sampleCurve(curve, at) {
  if (at <= curve[0][0]) return curve[0][1];
  const last = curve[curve.length - 1];
  if (at >= last[0]) return last[1];
  for (let i = 1; i < curve.length; i++) {
    const [x1, y1] = curve[i - 1];
    const [x2, y2] = curve[i];
    if (at > x2) continue;
    return y1 + ((y2 - y1) * (at - x1)) / (x2 - x1);
  }
  return last[1];
}

/**
 * `against` answers who this fighter is currently facing. Every event carries
 * an opponent, and most of them — a taunt, a run of the same jab, backing away
 * — happen *at* somebody without anybody passing a name in, so there has to be
 * one place to ask. A function rather than a value because a fighter's opponent
 * is not decided when they are built.
 */
export function createHeat({ id = null, against = () => null, ...overrides } = {}) {
  const config = Object.freeze({
    ...HEAT,
    ...overrides,
    events: Object.freeze({ ...HEAT.events, ...overrides.events }),
    levels: Object.freeze({ ...HEAT.levels, ...overrides.levels }),
    flee: Object.freeze({ ...HEAT.flee, ...overrides.flee }),
    repetition: Object.freeze({ ...HEAT.repetition, ...overrides.repetition }),
    momentum: Object.freeze({ ...HEAT.momentum, ...overrides.momentum }),
  });
  validateHeat(config);

  let value = config.initial;
  let clock = 0;
  const listeners = new Set();

  /** Newest first, oldest pushed off the end. */
  let events = [];

  /** Seconds the fighter has been backing away without a break. */
  let retreating = 0;
  /** Seconds until fleeing can be charged again. */
  let fleeArmed = 0;

  /** Recent strikes, newest last, as `[name, at]`. Only long enough to spot a run. */
  let thrown = [];
  let repetitionArmed = 0;

  function push(type, { opponent, move = null } = {}) {
    const delta = clamp(value + config.events[type], 0, config.max) - value;
    value += delta;

    const event = Object.freeze({
      type,
      opponent: opponent ?? against() ?? null,
      move,
      delta,
      at: clock,
      rise: delta > 0,
    });
    // A rolling list, not a log. Five is enough to read the shape of a round
    // and short enough that the overlay does not become a wall of text.
    events = [event, ...events].slice(0, config.history);
    for (const listener of listeners) listener(event);
    return event;
  }

  return {
    config,
    id,

    get value() {
      return value;
    },
    get max() {
      return config.max;
    },
    get fraction() {
      return value / config.max;
    },

    /**
     * How fast this fighter's momentum meter fills, as a multiplier.
     *
     * Read at the moment of the gain rather than cached, because a counter
     * thrown in a hot fight should pay what the fight is worth *now*. Stacks
     * with charisma; both are multipliers and neither is the other's business.
     */
    get momentumScale() {
      return sampleCurve(config.momentum.curve, value);
    },

    /**
     * `cold`, `warm` or `hot`. The bands the crowd reads, and the only part of
     * heat anything outside this module should be branching on — a threshold
     * compared against the raw number in three places is three places to edit.
     */
    get level() {
      return levelOf(value, config);
    },

    /** What has happened lately, newest first. Frozen entries; safe to hold. */
    get events() {
      return events;
    },
    /** The last thing that moved the meter, or null. */
    get last() {
      return events[0] ?? null;
    },

    /**
     * Records one of the nine things that move heat.
     *
     * `opponent` is who it happened against, because "landed a counter" and
     * "landed a counter on the fighter who has been running all round" are
     * different stories and the list is there to tell one.
     */
    record(type, detail = {}) {
      if (!SOURCES.includes(type)) return null;
      return push(type, detail);
    },

    /**
     * A strike was thrown. Heat only cares whether it has seen it before.
     *
     * Called on the throw rather than on the hit, deliberately: throwing the
     * same move over and over is dull whether or not it is connecting, and a
     * fighter who has found one thing that works is exactly the case this is
     * meant to notice.
     */
    threw(move, { opponent } = {}) {
      if (!move) return null;
      thrown = [...thrown, { move, at: clock }].filter(
        (entry) => clock - entry.at <= config.repetition.window
      );

      if (repetitionArmed > 0) return null;
      const run = config.repetition.runOf;
      if (thrown.length < run) return null;
      // The *last* `run` throws, all the same. A fighter mixing jab-cross-jab
      // is not repeating themselves; three jabs is.
      const recent = thrown.slice(-run);
      if (!recent.every((entry) => entry.move === move)) return null;

      repetitionArmed = config.repetition.cooldown;
      return push('repetition', { move, opponent });
    },

    update(dt, { retreating: backingAway = false } = {}) {
      if (!(dt > 0)) return value;
      clock += dt;

      if (fleeArmed > 0) fleeArmed = Math.max(0, fleeArmed - dt);
      if (repetitionArmed > 0) repetitionArmed = Math.max(0, repetitionArmed - dt);

      // Backing off for a moment is spacing. Backing off for two seconds is
      // running away, and it keeps costing for as long as it goes on.
      if (backingAway) {
        retreating += dt;
        if (retreating >= config.flee.after && fleeArmed <= 0) {
          fleeArmed = config.flee.every;
          push('flee');
        }
      } else {
        retreating = 0;
      }

      // And the drift. Heat has a resting point rather than a floor, so a rise
      // fades and a fall recovers — otherwise one dull knockdown would price a
      // fighter out of the crowd for the rest of the night.
      const rest = config.baseline;
      if (value !== rest && config.decayPerSecond > 0) {
        const step = config.decayPerSecond * dt;
        value = value > rest ? Math.max(rest, value - step) : Math.min(rest, value + step);
      }
      return value;
    },

    /** Fires for every event, in the order they happened. */
    onEvent(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    reset() {
      value = config.initial;
      clock = 0;
      events = [];
      retreating = 0;
      fleeArmed = 0;
      thrown = [];
      repetitionArmed = 0;
    },

    /** Everything worth carrying across a reload: the number and the reasons. */
    snapshot() {
      return Object.freeze({ value, events: events.map((event) => ({ ...event })) });
    },

    /**
     * Puts a saved meter back.
     *
     * Only the value and the event list. The armed timers — how long this
     * fighter has been backing away, how close they are to a repetition — are
     * deliberately *not* restored: they are about the last few seconds of a
     * round that is no longer happening, and reloading into a half-armed flee
     * timer would fire a heat event for a retreat nobody made.
     */
    restore({ value: saved = null, events: list = [] } = {}) {
      if (Number.isFinite(saved)) value = clamp(saved, 0, config.max);
      events = list.slice(0, config.history).map((event) => Object.freeze({ ...event }));
      return value;
    },
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
